import React, { type FC, useCallback, forwardRef, useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { N300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipPrimitiveProps, TooltipPrimitive } from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { intentTemplate_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar$key } from '@atlassian/jira-relay/src/__generated__/intentTemplate_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar.graphql';
import { INTENT_TEMPLATE_TYPES, type IntentTemplateType } from '../../../common/types';

import { TemplateChangeModal } from '../../../common/ui/template-change-modal';
import { TemplateIcon } from '../../../common/ui/template-icon';
import { TemplateLozenge } from '../../../common/ui/template-lozenge';
import {
	useIntentTemplateStoreActions,
	useIntentTemplateStoreState,
} from '../../../controllers/intent-template-store';
import messages from './messages';
import { TemplateDialog } from './template-dialog';

export const CustomTooltip = forwardRef<HTMLDivElement, TooltipPrimitiveProps>((props, ref) => (
	<TooltipContainer {...props} ref={ref} />
));

export const IntentTemplate: FC<{
	intentTemplateDataRef: intentTemplate_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar$key;
	onSelect: () => void;
}> = ({ intentTemplateDataRef, onSelect }) => {
	const { formatMessage } = useIntl();
	const intentTemplateData =
		useFragment<intentTemplate_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar$key>(
			graphql`
				fragment intentTemplate_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar on VirtualAgentIntentTemplate {
					id
					name
					type
					description
					...templateDialog_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar
				}
			`,
			intentTemplateDataRef,
		);

	const { id: selectedTemplateId, isDirty } = useIntentTemplateStoreState();

	const { setIntentTemplate } = useIntentTemplateStoreActions();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const openModal = useCallback(() => setIsModalOpen(true), []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);

	const { id, type, name, description } = intentTemplateData;
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const templateType = type in INTENT_TEMPLATE_TYPES ? (type as IntentTemplateType) : undefined;

	const onSubmit = useCallback(() => {
		// immediately set the already available props
		setIntentTemplate({
			id,
			name,
			type: templateType,
			description,
		});
		closeModal();

		onSelect();
	}, [closeModal, description, id, name, onSelect, setIntentTemplate, templateType]);

	const onClick = useCallback(() => {
		if (isDirty && selectedTemplateId) {
			openModal();
		} else {
			onSubmit();
		}
	}, [isDirty, selectedTemplateId, openModal, onSubmit]);

	return (
		<>
			<Tooltip
				component={CustomTooltip}
				content={<TemplateDialog intentTemplateDataRef={intentTemplateData} />}
				position="left"
				testId="servicedesk-settings-virtual-agent-create-intent-templates-sidebar.ui.content.intent-template.tooltip"
			>
				{(tooltipProps) => (
					<IntentTemplateContainer
						role="button"
						tabIndex={0}
						{...tooltipProps}
						onClick={onClick}
						selected={selectedTemplateId === intentTemplateData.id}
					>
						<TemplateIcon type={templateType} />
						<ContentContainer>
							<HeadingContainer>
								<Heading size="xsmall">{intentTemplateData.name}</Heading>
								<TemplateLozenge type={templateType} />
							</HeadingContainer>
							{intentTemplateData.description != null && (
								<DescriptionText>{intentTemplateData.description}</DescriptionText>
							)}
						</ContentContainer>
					</IntentTemplateContainer>
				)}
			</Tooltip>
			<TemplateChangeModal
				isOpen={isModalOpen}
				onClose={closeModal}
				onSubmit={onSubmit}
				title={formatMessage(messages.changeTemplateModalTitle)}
				buttonText={formatMessage(messages.changeTemplateModalConfirmButtonText)}
			/>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IntentTemplateContainer = styled.div<{ selected: boolean }>({
	display: 'flex',
	padding: `${token('space.300', '24px')} ${token('space.200', '16px')}`,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral.subtle.hovered', colors.N20),
	},
	cursor: 'pointer',
	borderRadius: token('border.radius.100', '4px'),
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: ({ selected }) => selected && token('color.background.selected', colors.B50),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderColor: ({ selected }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		selected ? token('color.border.selected', colors.B100) : 'transparent',
	gap: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.100', '8px'),
	alignItems: 'baseline',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionText = styled.p({
	color: token('color.text.accent.gray', N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TooltipContainer = styled(TooltipPrimitive)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.overlay', colors.N0),
	borderRadius: token('border.radius.100', '3px'),
	boxShadow: token(
		'elevation.shadow.overlay',
		'0px 8px 12px 0px rgba(9, 30, 66, 0.15), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)',
	),
	boxSizing: 'content-box',
	width: '400px',
});
